import { Maybe } from 'monet';
import {
  AggregatorAppointmentRequest,
  AppointmentExistPatientCreationRequest,
  AppointmentNewPatientCreationRequest,
  TelephonyCallAppointmentRequest,
} from '@/storage/gateways/AppointmentScheduleGateway';
import { Waiting } from '@/storage/gateways/WaitingListGateway';
import {
  APPOINTMENT_COPY, APPOINTMENT_ORDERS_COUNT, CLEAR_APPOINTMENT_COPY, LOAD_APPOINTMENT_COPY,
} from '@/store/actions/appointments';
import callMixin from '@/mixins/callMixin';
import {
  convertToInputDate,
  normalizeIso8601ToDateTimeStringFormat,
} from '@/utils/timeFormat';
import { GOAL_CHANGE_APPOINTMENT_TIMELINE_STEP } from '@/plugins/metric';
import appointmentCreationMixin from '@/mixins/appointmentCreationMixin';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import debounce from 'lodash.debounce';

export const APPOINTMENT_COPY_KEY = 'appointment-copy';

const ACTION_CHANGE_APPOINTMENT_STATUS = 'change-appointment-status';

const ACTION_VIEW_PATIENT_MEDICAL_CARD = 'view-patient-medical_card';

const EMPTY_INPUT_ERRORS = {};

/**
 * @description Слот расписания.
 * @param {string[]}    actions
 * @param {string[]}    tags
 * @param {string}      isoDate
 * @param {string}      startsTime
 * @param {string}      endsTime
 * @param {?number}     appointmentId
 * @param {?number}     employeeId
 * @param {Identifier}  cabinetId
 * @param {?number}     patientId
 * @param {?boolean}    hasPrintNextAppointments
 */
export function AppointmentSlot(
  actions,
  tags,
  isoDate,
  startsTime,
  endsTime,
  appointmentId = null,
  employeeId = null,
  cabinetId = null,
  patientId = null,
  hasPrintNextAppointments = false,
) {
  const hasAction = (actionId) => actions.includes(actionId);
  this.isOpenSlot = () => tags.includes('open') || tags.includes('add');
  this.isPatientSlot = () => tags.includes('patient') || tags.includes('busy');
  this.canChangeAppointmentStatus = () => (
    this.isPatientSlot() && hasAction(ACTION_CHANGE_APPOINTMENT_STATUS)
  );
  this.canViewPatientMedicalCard = () => (
    this.isPatientSlot() && hasAction(ACTION_VIEW_PATIENT_MEDICAL_CARD)
  );

  this.getAppointmentId = () => appointmentId;
  this.getCabinetId = () => cabinetId;
  this.getEmployeeId = () => employeeId;
  this.getPatientId = () => patientId;
  this.getEndsTime = () => endsTime;
  this.getIsoDate = () => isoDate;
  this.getStartsTime = () => startsTime;
  this.hasPrintNextAppointments = () => hasPrintNextAppointments;

  this.copyWithProps = (props) => new this.constructor(
    actions,
    tags,
    isoDate,
    startsTime,
    props.endsTime === undefined ? endsTime : props.endsTime,
    appointmentId,
    props.employeeId === undefined ? employeeId : props.employeeId,
    cabinetId,
    patientId,
    hasPrintNextAppointments,
  );
}

/**
 * @description Получить временную разницу в минутах для строкового времени (HH:ii).
 * @param {string} startsTime Начальное время.
 * @param {string} endsTime   Конечное время.
 * @returns {number}
 */
export function calcDifferenceMinutesForStringTime(startsTime, endsTime) {
  const toStamp = (time) => {
    const [numHours, numMinutes] = [
      Number(time.split(':')[0]),
      Number(time.split(':')[1]),
    ];
    return (numHours * 60) + numMinutes;
  };
  const [minStamp, maxStamp] = [startsTime, endsTime].sort().map(toStamp);
  return maxStamp - minStamp;
}

/**
 * @description Примесь допустимых действий.
 * @type {Vue}
 */
const actionsMixin = {
  computed: {
    /**
     * @description Может создавать пациентов.
     * @returns {boolean}
     */
    canCreatePatients() {
      return this.actions.includes('create-patients');
    },
    /**
     * @description Может просматривать телефоны пациентов.
     * @returns {boolean}
     */
    hasViewPatientPhone() {
      return this.actions.includes('view-patient-phone');
    },
    /**
     * @description Может просматривать мед. карты пациентов.
     * @returns {boolean}
     */
    hasViewPatientMedicalCard() {
      return this.actions.includes('view-patient-medical-card');
    },
    /**
     * @description Может просматривать счета пациентов.
     * @returns {boolean}
     */
    hasViewPatientInvoices() {
      return this.actions.includes('view-patient-invoices');
    },
    /**
     * @description Может просматривать визит пациента.
     * @returns {boolean}
     */
    hasViewPatientVisit() {
      return this.actions.includes('view-patient-visit');
    },
  },
  data() {
    return {
      /**
       * @description Допустимые действия.
       * @type {string}
       */
      actions: [],
    };
  },
};

/**
 * @description Примесь заявок записи на прием от мед. агрегаторов.
 * @type {object}
 */
const aggregatorOrdersMixin = {
  computed: {
    aggregator() {
      return this.selectedAggregator.fold({})(
        ({ cancelable, name, orders }) => ({ cancelable, name, orders }),
      );
    },
    aggregatorDialogLabel() {
      return this.selectedAggregator.fold('')(
        ({ name }) => `Записи с ${name}`,
      );
    },
    /**
     * @description Показывать заявки от агрегатора.
     * @returns {boolean}
     */
    shownAggregatorOrdersDialog() {
      return this.selectedAggregator.isSome();
    },
  },
  data() {
    return {
      /**
       * @description Заявки записи на прием от мед. агрегаторов.
       * @type {any[]}
       */
      ordersByAggregators: this.initialOrders,
      /**
       * @description Выбранный агрегатор.
       * @type {Maybe<object>}
       */
      selectedAggregator: Maybe.none(),
    };
  },
  methods: {
    openAggregatorOrdersDialog(aggregator) {
      this.selectedAggregator = Maybe.some(aggregator);
    },
    closeAggregatorOrdersDialog() {
      this.selectedAggregator = Maybe.none();
    },
    /**
     * @description Получить заявки на прием по агрегаторам.
     * @returns {void}
     */
    fetchAggregatorOrders() {
      this.$appointmentScheduleGateway.getAggregatorOrders().then((result) => {
        result.forEach((response) => {
          this.ordersByAggregators = response.aggregators;
          if (this.shownAggregatorOrdersDialog) {
            this.selectedAggregator = Maybe.fromEmpty(
              response.aggregators.find((candidate) => candidate.name === this.aggregator.name),
            );
          }
          const count = response.aggregators
            .reduce((acc, aggregator) => acc + aggregator.orders.length, 0);
          this.$store.commit(APPOINTMENT_ORDERS_COUNT, count);
        });
      });
    },
    /**
     * @description Обработать создание записи на прием из заявки от агрегатора.
     * @param {number} orderId Идентификатор заявки.
     * @returns {void}
     */
    onMakeAppointmentFromAggregatorOrder(orderId) {
      const onFail = (response) => {
        this.notifyFail(response.title);
      };
      const onSuccess = (order) => {
        this.closeAggregatorOrdersDialog();
        this.appointmentInputErrors = EMPTY_INPUT_ERRORS;
        this.appointmentEmployee = Maybe.some({
          id: order.employee_id,
          name: '',
        });
        this.onInputAppointmentTimes(order.starts_time, order.ends_time);
        this.onInputAppointmentEmployee(order.employee_id);
        const dateTime = new Date(
          normalizeIso8601ToDateTimeStringFormat(`${order.date} ${order.starts_time}`),
        );
        this.onInputAppointmentDate(dateTime);
        this.onReloadEmployeeDayTimeline(order.employee_id, order.date, order.starts_time);
        this.$nextTick(() => {
          this.selectedAppointment = Maybe.some({
            actions: this.actions,
            aggregatorName: order.aggregator_name,
            aggregatorOrderId: order.id,
            dateTime,
            isOutdated: order.tags.includes('outdated'),
            name: order.patient_name,
            needsCreatingPatient: true,
            phone: this.normalizePrefixPhoneNumber(order.patient_phone),
            surname: order.patient_surname,
          });
        });
      };
      this.$appointmentScheduleGateway.getAggregatorOrder(orderId).then((response) => {
        response.forEach(onSuccess);
        response.forEachLeft(onFail);
      });
    },
    /**
     * @description Обработать отмену заявки от агрегатора.
     * @param {number} orderId Идентификатор заявки.
     * @returns {void}
     */
    onCancelAppointmentOrderFromAggregator(orderId) {
      const onSuccess = (data) => {
        this.notifySuccess(data.title);
        this.fetchAggregatorOrders();
      };
      /**
       * @param {LeftResponse} leftResponse
       */
      const onFail = (leftResponse) => {
        this.notifyFail(leftResponse.getTitle());
      };
      this
        .$appointmentScheduleGateway
        .cancelAggregatorOrder(orderId)
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
  },
  props: {
    initialOrders: {
      default: () => [],
      type: Array,
    },
  },
};

/**
 * @description Примесь копирования записи на прием.
 * @type {any}
 */
const appointmentCopyMixin = {
  created() {
    window.addEventListener('storage', this.onStorageChanged);
    this.$once(
      'hook:beforeDestroy',
      () => window.removeEventListener('storage', this.onStorageChanged),
    );
    this.$store.dispatch(LOAD_APPOINTMENT_COPY);
  },
  computed: {
    /**
     * @description Скопированная запись на прием.
     * @type {any}
     */
    appointmentCopy: ({ $store }) => $store.getters.getAppointmentCopy,
    /**
     * @description Отключить действие вставки скопированной записи на прием.
     * @type {boolean}
     */
    disabledPasteAppointmentAction() {
      const isOpenSlot = this.appointmentSlotTarget.fold(false)((slot) => slot.isOpenSlot());
      return !this.isPasteAppointmentAction || !isOpenSlot;
    },
    /**
     * @description Возможно ли действие вставки скопированной записи на прием.
     * @type {boolean}
     */
    isPasteAppointmentAction({ appointmentCopy }) {
      const isOpenSlot = this.appointmentSlotTarget.fold(false)((slot) => slot.isOpenSlot());
      return !!appointmentCopy && isOpenSlot;
    },
    /**
     * @description Возможно ли действие копирования записи на прием.
     * @type {boolean}
     */
    isCopyAppointmentAction({ appointmentCopy }) {
      const isPatientSlot = this.appointmentSlotTarget
        .fold(false)((slot) => slot.isPatientSlot());
      return isPatientSlot && !appointmentCopy;
    },
    /**
     * @description Возможно ли изменят статус слота.
     * @type {boolean}
     */
    isChangeStatusAvailable() {
      return this.appointmentSlotTarget.fold(false)((slot) => slot.canChangeAppointmentStatus());
    },
    /**
     * @description Возможно ли перейти в мед. карту пациента.
     * @return {boolean}
     */
    isGoToPatientMedicalCard() {
      const isPatientSlot = this.appointmentSlotTarget
        .fold(false)((slot) => slot.isPatientSlot());
      const existPatientId = this.appointmentSlotTarget
        .fold(false)((slot) => slot.getPatientId() !== null);
      const canViewPatientMedicalCard = this.appointmentSlotTarget
        .fold(false)((slot) => slot.canViewPatientMedicalCard());
      return isPatientSlot && existPatientId && canViewPatientMedicalCard;
    },
    /**
     * @description Возможна ли печать будущих записей пациента.
     * @return {boolean}
     */
    isPrintFutureAppointments() {
      const isPatientSlot = this.appointmentSlotTarget
        .fold(false)((slot) => slot.isPatientSlot());
      const existPatientId = this.appointmentSlotTarget
        .fold(false)((slot) => slot.getPatientId() !== null);
      const hasPrintFutureAppointments = this.appointmentSlotTarget
        .fold(false)((slot) => slot.hasPrintNextAppointments());
      return isPatientSlot && existPatientId && hasPrintFutureAppointments;
    },
    /**
     * Отключение контекстного меню при отсутствии пунктов
     * @returns {boolean}
     */
    disableContextMenu() {
      return !this.isCopyAppointmentAction
        && !this.isChangeStatusAvailable
        && !this.isPasteAppointmentAction
        && !this.isPrintFutureAppointments;
    },
  },
  data() {
    return {
      /**
       * @description Слот для вставки заявки
       * @type {Object}
       */
      appointmentSlotForPaste: {},
      /**
       * @description Слот для которого отображается контекстное меню.
       * @type {Maybe<AppointmentSlot>}
       */
      appointmentSlotTarget: Maybe.none(),
      /**
       * @description Узел к которому прикрепляется контекстное меню.
       * @type {Element}
       */
      contextMenuTarget: null,
      /**
       * @description Нужно подтверждение для вставки
       * @type {boolean}
       */
      needConfirmAppointmentPaste: false,
      /**
       * @description Показывать контекстное меню слота.
       * @type {boolean}
       */
      isShowSlotContextMenu: false,
      /**
       * @description Смещение контекстного меню относительно родителя по горизонтали.
       * @type {number}
       */
      offsetContextMenuX: 0,
      /**
       * @description Смещение контекстного меню относительно родителя по вертикали.
       * @type {number}
       */
      offsetContextMenuY: 0,
      /**
       * @description Отображать модальное окно отмены записи.
       * @type {boolean}
       */
      isShowCancelModal: false,
      /**
       * @description Текущий статус записи.
       * @type {string}
       */
      currentStatus: '',
      /**
       * @description Ошибки изменения статуса заявки.
       * @type {Object}
       */
      changeStatusInputErrors: {},
    };
  },
  methods: {
    /**
     * @description Проверить наличие рабочих часов у сотрудника в кабинете.
     * @param {number} cabinetId  Идентификатор кабинета.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {string} isoDate    Дата в формате ISO.
     * @returns {Promise<boolean>}
     */
    checkEmployeeIsWorkInCabinet(cabinetId, employeeId, isoDate) {
      const onSuccess = (employees) => employees.some((employee) => employee.id === employeeId);
      return this.$appointmentScheduleGateway
        .viewWorkingEmployeeListByCabinet(cabinetId, isoDate, 'day')
        .then((result) => result.fold([])(onSuccess));
    },
    /**
     * @description Поменять статус записи
     * @param {String} status
     * @param {String} cause
     * @param {String} rejectionReasonId
     * @param {String} customRejectionReason
     */
    changeAppointmentStatus(
      status,
      cause = '',
      rejectionReasonId = '',
      customRejectionReason = '',
    ) {
      this.appointmentSlotTarget.forEach((slot) => {
        const id = slot.getAppointmentId();
        switch (status) {
          case 'came':
            this.onCameAppointment(id);
            break;
          case 'cancel':
            this.onCancelAppointment(id, cause, rejectionReasonId, customRejectionReason);
            break;
          case 'confirm':
            this.onConfirmAppointment(id);
            break;
          case 'noconfirm':
            this.onNoConfirmAppointment(id);
            break;
          default:
            break;
        }
      });
    },
    /**
     * @description закрыть диалог подтверждения вставки заявки
     * @returns {void}
     */
    closeConfirmPasteDialog() {
      this.needConfirmAppointmentPaste = false;
    },
    /**
     * @description Получить время окончания по старту и продолжительности
     * @param {string} startsTime
     * @param {int} duration
     * @return {string}
     */
    getEndsTimeByStartAndDuration(startsTime, duration) {
      const [startsHours, startsMinutes] = startsTime.split(':');
      const endsDate = new Date();
      endsDate.setHours(+startsHours, +startsMinutes + duration, 0, 0);
      const hours = String(endsDate.getHours()).padStart(2, '0');
      const minutes = String(endsDate.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    /**
     * @description Открыть форму для редактирования скопированной записи на прием.
     * @param {any}             appointment
     * @param {AppointmentSlot} slot
     * @returns {void}
     */
    makeAppointmentFromCopy(appointment, slot) {
      this.appointmentCabinetId = slot.getCabinetId();
      this.selectedAppointment = Maybe.none();
      this.onInputAppointmentTimes(slot.getStartsTime(), slot.getEndsTime());
      const employeeId = slot.getEmployeeId();
      if (employeeId && employeeId > 0) {
        this.appointmentEmployee = Maybe.some({ id: employeeId, name: '' });
        this.onInputAppointmentEmployee(employeeId);
        this.onReloadEmployeeDayTimeline(
          employeeId,
          slot.getIsoDate(),
          slot.getStartsTime(),
        );
      } else {
        this.appointmentEmployee = Maybe.none();
        this.onInputAppointmentEmployee(null);
      }
      this.onInputAppointmentDate(
        new Date(normalizeIso8601ToDateTimeStringFormat(slot.getIsoDate())),
      );
      this.selectedAppointment = Maybe.some({
        actions: this.actions,
        name: appointment.patientName,
        needsCreatingPatient: appointment.needsCreatingPatient,
        patientId: appointment.patientId,
        phone: appointment.patientPhone,
        services: appointment.serviceIds.map((value) => ({ value })),
        surname: appointment.patientSurname,
      });
    },
    /**
     * @description Обработать копирование визита.
     * @param {any} appointment Запись на прием.
     * @returns {void}
     */
    onCopy(appointment) {
      this.$store.dispatch(APPOINTMENT_COPY, appointment);
      this.onCloseAppointmentForm();
      this.notifySuccessLong(
        'Запись скопирована.'
        + ' Нажмите правой кнопкой мыши на нужный слот времени и вставьте запись.',
      );
    },
    /**
     * @description Обработать переход к медицинской карте пациента
     * @returns {void}
     */
    async onPatientMedicalCard() {
      await this.$router.push({
        name: 'ClinicPanelPatientBasic',
        params: {
          id: this.appointmentSlotTarget.fold(false)((slot) => slot.getPatientId()),
        },
      });
    },
    /**
     * @description Обработать печать будущих записей
     * @returns {Promise<void>}
     */
    onPrintFutureAppointments() {
      const patientId = this.appointmentSlotTarget.fold(false)((slot) => slot.getPatientId());
      this.printFutureAppointments(patientId).then((result) => {
        if (result === null) {
          return;
        }
        const blob = new Blob([result], { type: 'text/html' });
        const container = this.$refs.printView;
        container.setAttribute('src', URL.createObjectURL(blob));
        container.onload = () => {
          container.contentWindow.print();
        };
      });
    },
    /**
     * @description Обработать открытие формы отмены визита.
     * @returns {void}
     */
    onShowCancelDialog() {
      this.isShowCancelModal = true;
      this.changeStatusInputErrors = {};
    },
    /**
     * @description Скопировать запись на прием по идентификатору.
     * @param {number} appointmentId
     * @param {number} employeeId
     * @param {number} timeIntervalInMinutes
     * @returns {void}
     */
    copyAppointmentById(appointmentId, employeeId, timeIntervalInMinutes = 0) {
      this
        .fetchAppointmentFromView(employeeId, appointmentId)
        .then((appointment) => {
          if (appointment.id) {
            const appointmentCopy = {
              advertisingChannelId: appointment.advertisingChannel.id,
              employeeId,
              needsCreatingPatient: false,
              note: appointment.note,
              patientBirthDate: '',
              patientId: appointment.patientId,
              patientName: '',
              patientPatronymic: '',
              patientPhone: '',
              patientSurname: '',
              serviceIds: appointment.services.map((service) => service.value),
              intervalDurationInMinutes: timeIntervalInMinutes,
            };
            this.onCopy(appointmentCopy);
          }
        });
    },
    /**
     * @description Обработать вставку скопированной записи на прием.
     * @param {AppointmentSlot} slot
     * @returns {void}
     */
    onPasteAppointment(slot) {
      this.closeConfirmPasteDialog();
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.reloadTimetable(this.viewQuery);
        this.fetchPatientList();
        this.$store.dispatch(CLEAR_APPOINTMENT_COPY);
      };
      const onFail = (response) => {
        this.notifyFail(response.title);
        const failurePairs = response.causes.map((cause) => [cause.source, cause.details]);
        this.appointmentInputErrors = Object.fromEntries(failurePairs) || {};
        this.makeAppointmentFromCopy(this.appointmentCopy, slot);
      };
      if (this.appointmentCopy.patientId !== null) {
        const request = new AppointmentExistPatientCreationRequest(
          this.appointmentCopy.advertisingChannelId,
          slot.getIsoDate(),
          slot.getEmployeeId(),
          slot.getEndsTime(),
          false,
          this.appointmentCopy.note,
          this.appointmentCopy.patientId,
          this.appointmentCopy.serviceIds,
          slot.getStartsTime(),
        );
        this.sendAppointmentExistsPatientRequest(request).then((result) => {
          result.fold(onFail, onSuccess);
        });
      } else {
        const request = new AppointmentNewPatientCreationRequest(
          this.appointmentCopy.advertisingChannelId,
          this.appointmentCopy.patientBirthAt,
          slot.getIsoDate(),
          slot.getEmployeeId(),
          slot.getEndsTime(),
          false,
          this.appointmentCopy.patientName,
          this.appointmentCopy.note,
          this.appointmentCopy.patientPatronymic,
          this.appointmentCopy.patientPhone,
          this.appointmentCopy.serviceIds,
          slot.getStartsTime(),
          this.appointmentCopy.patientSurname,
        );
        this.sendAppointmentNewPatientCreationRequest(request).then((result) => {
          result.fold(onFail, onSuccess);
        });
      }
    },
    /**
     * @description Обработать изменение локального хранилища из другого окна.
     * @param {StorageEvent} event Событие.
     * @returns {void}
     */
    onStorageChanged(event) {
      if (event.key === APPOINTMENT_COPY_KEY) {
        this.$store.dispatch(APPOINTMENT_COPY, JSON.parse(event.newValue));
      }
    },
    /**
     * @description Открыть контекстное меню слота записи на прием.
     * @param {{x:number,y:number}} offset        Смещение.
     * @param {string}              currentStatus Текущий статус записи.
     * @param {AppointmentSlot}     slot          Слот расписания.
     * @param {Element}             target        DOM элемент.
     */
    openAppointmentSlotContextMenu(offset, currentStatus, slot, target) {
      this.appointmentSlotTarget = Maybe.fromEmpty(slot);
      this.contextMenuTarget = target;
      this.offsetContextMenuX = offset.x;
      this.offsetContextMenuY = offset.y;
      this.currentStatus = currentStatus;
      this.isShowSlotContextMenu = true;
    },
    /**
     * @description Подготовить допустимую продолжительность для вставки заявки
     * @param {Array} slots Проверяемые слоты
     * @param {string} startsTime Время начала
     * @param {int} targetDuration Целевая продолжительность в минутах
     * @return {*|number}
     */
    prepareEndsTimeDurationForPasteAppointment(slots, startsTime, targetDuration) {
      const getAvailableMinutesBySlots = (durationInMinutes = 0, i = 0) => {
        if (durationInMinutes >= targetDuration) {
          return targetDuration;
        }
        let index = i;
        const slot = slots[index];
        if (slot.patient === null) {
          const slotTimeDuration = calcDifferenceMinutesForStringTime(
            slot.starts_time,
            slot.ends_time,
          );
          index += 1;
          return getAvailableMinutesBySlots(durationInMinutes + slotTimeDuration, index);
        }
        return durationInMinutes;
      };
      return getAvailableMinutesBySlots();
    },
  },
};

/**
 * @description Примесь ближайших неподтвержденных записей на прием.
 * @type {object}
 */
const nearestUnconfirmedAppointmentMixin = {
  computed: {
    nearestUnconfirmedAppointmentCount() {
      return this.nearestUnconfirmedAppointments.length;
    },
  },
  data() {
    return {
      /**
       * @description Действия над ближайшими неподтвержденными записями.
       * @type {string[]}
       */
      nearestUnconfirmedAppointmentActions: [],
      /**
       * @description Ближайшие неподтвержденные записи
       * @type {Array}
       */
      nearestUnconfirmedAppointments: [],
      /**
       * @description Показывать список ближайших неподтвержденных записей на прием.
       * @type {boolean}
       */
      shownNearestUnconfirmedAppointmentList: false,
    };
  },
  methods: {
    /**
     * @description Закрыть список ближайших неподтвержденных записей.
     * @returns {void}
     */
    closeNearestUnconfirmedAppointmentList() {
      this.shownNearestUnconfirmedAppointmentList = false;
    },
    /**
     * @description Затянуть ближайшие неподтвержденные записи.
     * @returns {void}
     */
    fetchNearestUnconfirmedAppointments() {
      this.$appointmentScheduleGateway.getNearestUnconfirmedAppointments().then((result) => {
        result.forEach((response) => {
          if (this.shownNearestUnconfirmedAppointmentList) {
            this.shownNearestUnconfirmedAppointmentList = response.items.length;
          }
          this.nearestUnconfirmedAppointments = response.items;
          this.nearestUnconfirmedAppointmentActions = response.actions;
        });
      });
    },
    /**
     * @description Открыть список ближайших неподтвержденных записей.
     * @returns {void}
     */
    openNearestUnconfirmedAppointmentList() {
      this.shownNearestUnconfirmedAppointmentList = true;
    },
  },
};

/**
 * @description Высота единичного шага.
 * @type {number}
 */
const STEP_HEIGHT = 53;

/**
 * @description Примесь для управления учетом рабочего времени.
 * @type {object}
 */
const workTimeControlsMixin = {
  data() {
    return {
      employeesForWorkTimeControls: [],
      messageForWorkTimeControls: '',
      shownWorkTimeControlsDialog: false,
    };
  },
  methods: {
    /**
     * @description Закрыть диалог управления рабочим временем сотрудников.
     * @returns {void}
     */
    closeWorkTimeControlsDialog() {
      this.shownWorkTimeControlsDialog = false;
    },
    /**
     * @description Получить сотрудников для учета рабочего времени.
     * @returns {Promise}
     */
    fetchEmployeesForWorkTimeControls() {
      return this
        .$workTimeControlsGateway
        .viewEmployeeWorkTimeControls()
        .then(async (result) => {
          result.forEachLeft((error) => {
            const { title } = error;
            const tags = error.tags || [];
            if (tags.includes('off-day')) {
              this.employeesForWorkTimeControls = [];
              this.messageForWorkTimeControls = title;
            } else {
              this.notifyFail(title);
            }
          });
          result.forEach(({ employees }) => {
            this.employeesForWorkTimeControls = employees;
            this.messageForWorkTimeControls = '';
          });
        });
    },
    /**
     * @description Запустить счетчик учета времени по сотруднику.
     * @param {number}      employeeId     Идентификатор сотрудника.
     * @param {number|null} doctorAssistId Идентификатор ассистируемого доктора.
     * @param {string}      date           Дата старта.
     * @returns {void}
     */
    startEmployeeWorkTimeCounter(employeeId, doctorAssistId, date) {
      this
        .$workTimeControlsGateway
        .startEmployeeWorkTimeCounter(employeeId, doctorAssistId, date)
        .then((result) => {
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.fetchEmployeesForWorkTimeControls();
          });
        });
    },
    /**
     * @description Остановить счетчик учета времени по сотруднику.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {string} date Дата остановки.
     * @returns {void}
     */
    stopEmployeeWorkTimeCounter(employeeId, date) {
      this
        .$workTimeControlsGateway
        .stopEmployeeWorkTimeCounter(employeeId, date)
        .then((result) => {
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.fetchEmployeesForWorkTimeControls();
          });
        });
    },
    /**
     * @description Открыть диалог управления рабочим временем.
     * @returns {void}
     */
    toggleDialogWorkTimeControls() {
      if (this.shownWorkTimeControlsDialog) {
        this.shownWorkTimeControlsDialog = false;
      } else {
        this
          .fetchEmployeesForWorkTimeControls()
          .then(() => {
            this.shownWorkTimeControlsDialog = true;
          });
      }
    },
  },
};

/**
 * @description Примесь для управления списком свободных докторов.
 * @type {object}
 */
const freeDoctorControlsMixin = {
  data() {
    return {
      employeeFreeDates: [],
      shownFreeDoctorControlsDialog: false,
      todayDate: new Date(this.$options.today).toISOString().substring(0, 10),
      /**
       * Шаг приёма
       * @type {string}
       */
      timeStepMinutes: '',
    };
  },
  methods: {
    /**
     * @description Закрыть диалог управления списком свободных докторов.
     * @returns {void}
     */
    closeFreeDoctorControlsDialog() {
      this.shownFreeDoctorControlsDialog = false;
    },
    /**
     * Изменить время шага приёма
     * @param value {string}
     * @return void
     */
    onChangeTimeStepMinutes(value) {
      this.timeStepMinutes = value;
      this.fetchFreeDoctors();
    },
    /**
     * @description Получить список свободных докторов.
     * @returns {Promise}
     */
    fetchFreeDoctors() {
      const request = {
        time_step_minutes: this.timeStepMinutes,
        date: this.todayDate,
      };
      this.$appointmentScheduleGateway
        .viewFreeDoctors(request)
        .then(async (result) => {
          this.employeeFreeDates = result.data;
        });
    },
    /**
     * @description Открыть диалог управления рабочим временем.
     * @returns {void}
     */
    toggleFreeDoctorControls() {
      if (this.shownFreeDoctorControlsDialog) {
        this.shownFreeDoctorControlsDialog = false;
      } else {
        this.fetchFreeDoctors();
        this.shownFreeDoctorControlsDialog = true;
      }
    },
  },
};

/**
 * @description Примесь листа ожидания.
 * @type {object}
 */
const waitingListMixin = {
  computed: {
    /**
     * @description Идентификатор записи из листа ожидания.
     * @type {number}
     */
    initialWaitingId() {
      const waitingId = this.$route.query.waiting;
      return waitingId ? +waitingId : null;
    },
    /**
     * @description Идентификатор выбранного ожидания.
     * @returns {number|null}
     */
    selectedWaitingId() {
      return this.waiting?.id || null;
    },
    /**
     * @description Показать лист ожидания.
     * @type {boolean}
     */
    showWaitingList() {
      return Array.isArray(this.selectedWaitings);
    },
    /**
     * @description Заголовок формы добавления записи.
     * @returns {string}
     */
    waitingListFormTitle: ({ waiting }) => {
      if (waiting && waiting.id !== null) {
        return 'Запись из листа ожидания';
      }
      return 'Новая запись в лист ожидания';
    },
    /**
     * @description Списки пациентов для листа ожидания.
     * @type {any[]}
     */
    waitingListPatients() {
      return this
        .waitingListPatientList
        .orLazy(this.fetchWaitingListPatientList)
        .map((patient) => ({
          ...patient,
          text: `${patient.surnameWithInitials} ${patient.phone} ${patient.medcard}`,
        }));
    },
  },
  data() {
    return {
      /**
       * @description Ожидание завершения запроса к листу ожидания.
       * @type {boolean}
       */
      isAwaitWaitingRequest: false,
      /**
       * @description Пациенты загружаются.
       * @type {boolean}
       */
      isWaitingListLoadingPatientList: false,
      /**
       * @description Выбранные ожидания из листа.
       * @type {(WaitingItem[]|null)}
       */
      selectedWaitings: null,
      /**
       * @description Запись в листе ожидания.
       * @type {?Waiting}
       */
      waiting: null,
      /**
       * @description Лист ожидания.
       * @type {WaitingItem[]}
       */
      waitingList: [],
      /**
       * @description Действия над листом ожидания.
       * @type {string[]}
       */
      waitingListActions: [],
      /**
       * @description Нарушения ограничений при добавлении записи в лист ожидания.
       * @type {any}
       */
      waitingListInputErrors: {},
      /**
       * @description Свободное время для листа ожидания.
       * @type {any[]}
       */
      waitingOpenTimes: [],
      /**
       * @description Список пациентов для листа ожидания.
       * @type {any[]}
       */
      waitingListPatientList: Maybe.none(),
    };
  },
  methods: {
    debounceFetchWaitingListPatientList: debounce(
      function _(searchQuery) { this.fetchWaitingListPatientList(searchQuery); },
      300,
    ),
    /**
     * @description Закрыть форму добавление записи.
     * @returns {void}
     */
    closeWaitingForm() {
      this.waitingListInputErrors = {};
      this.waiting = null;
      this.waitingListPatientList = Maybe.none();
    },
    /**
     * @description Закрыть лист ожидания.
     * @returns {void}
     */
    closeWaitingList() {
      this.selectedWaitings = null;
    },
    /**
     * @description Закрыть выбор свободного времени для записи из листа ожидания.
     * @returns {void}
     */
    closeWaitingOpenTimes() {
      this.waitingOpenTimes = [];
    },
    /**
     * @description Получить лист ожидания.
     * @returns {void}
     */
    fetchWaitingList(afterAdd = false) {
      this
        .$waitingListGateway
        .viewDailyList(this.isoDate, this.period)
        .then((result) => {
          this.waitingList = result.map(({ list }) => list).orSome([]);
          if (afterAdd) {
            this.selectedWaitings = [...this.waitingList];
          }
          this.waitingListActions = result.map(({ actions }) => actions).orSome([]);
          if (this.initialWaitingId) {
            this.openWaitingList([this.initialWaitingId]);
          }
        });
    },
    /**
     * @description Получить свободное время для записи из листа ожидания.
     * @param {number} waitingId Идентификатор записи.
     * @returns {void}
     */
    fetchWaitingOpenTimes(waitingId) {
      this.$waitingListGateway.getOpenTimes(waitingId).then((result) => {
        this.waitingOpenTimes = result.toArray();
      });
    },
    /**
     * @description Обработать создание новой записи в листе ожидания.
     * @returns {void}
     */
    onCreateWaiting() {
      this.waitingListInputErrors = {};
      this.waiting = new Waiting();
    },
    /**
     * @description Обработать удаление записи из листа ожидания.
     * @param {number} id Идентификатор.
     * @returns {void}
     */
    async onDeleteWaiting(id) {
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = ({ message }) => {
        this.notifySuccess(message);
        this.fetchWaitingList(true);
      };
      const either = await this.$waitingListGateway.delete(id);
      either.fold(onFail, onSuccess);
    },
    /**
     * @description Обработать создание записи на прием из листа ожидания.
     * @param {number} id Идентификатор.
     * @returns {void}
     */
    async onMakeAppointmentFromWaitingList(id) {
      const onFail = (response) => {
        this.notifyFail(response.title);
      };
      const onSuccess = (waiting) => {
        this.closeWaitingList();
        this.appointmentInputErrors = EMPTY_INPUT_ERRORS;
        this.appointmentEmployee = Maybe.some({
          id: waiting.employeeId,
          name: waiting.employeeName,
        });
        this.onInputAppointmentTimes(waiting.timeStarts, waiting.timeEnds);
        const now = new Date();
        let startsDate = waiting.dateStarts;
        if (startsDate < now) {
          startsDate = now;
        }
        const dateTime = new Date(
          normalizeIso8601ToDateTimeStringFormat(
            `${getISODateStringByDate(startsDate)} ${waiting.timeStarts}`,
          ),
        );
        this.onInputAppointmentDate(dateTime);
        if (waiting.employeeId) {
          this.onInputAppointmentEmployee(waiting.employeeId);
          this.onReloadEmployeeDayTimeline(
            waiting.employeeId,
            getISODateStringByDate(startsDate),
            waiting.timeStarts,
          );
        }
        this.$nextTick(() => {
          this.selectedAppointment = Maybe.some({
            actions: this.actions,
            patientName: waiting.patientName,
            patientId: waiting.patientId,
            needsCreatingPatient: false,
            services: waiting.services.map((item) => ({
              duration: item.duration,
              text: `${item.name}, ${item.duration}`,
              title: item.name,
              value: item.id,
            })),
            dateTime,
            waitingId: waiting.id,
            patientFullName: waiting.patientFullName,
          });
        });
      };
      const either = await this.$waitingListGateway.getById(id);
      either.fold(onFail, onSuccess);
    },
    /**
     * @description Обработать поиск пациентов.
     * @param {string|null} searchQuery
     * @returns {void}
     */
    onSearchWaitingListPatients(searchQuery) {
      this.isWaitingListLoadingPatientList = true;
      this.debounceFetchWaitingListPatientList(searchQuery);
    },
    /**
     * @description Обработать запрос на добавление в лист ожидания существующего пациента.
     * @param {WaitingExistPatientRequest} request Запрос.
     * @returns {void}
     */
    onSubmitWaitingExistPatient(request) {
      const onResponse = (result) => {
        this.isAwaitWaitingRequest = false;
        result.forEach(({ message }) => {
          this.closeWaitingForm();
          this.notifySuccess(message);
          this.fetchWaitingList(true);
          this.fetchPatientList();
          this.selectedWaitings = this.waitingList;
        });
        result.forEachFail((error) => {
          this.waitingListInputErrors = Object.fromEntries(error.causes) || {};
          if (error.causes.length === 0) {
            this.notifyFail(error.title);
          }
        });
      };
      this.isAwaitWaitingRequest = true;
      const saving = this.selectedWaitingId !== null
        ? this.$waitingListGateway.editWithExistPatient(request, this.selectedWaitingId)
        : this.$waitingListGateway.createWithExistPatient(request);
      saving.then(onResponse);
    },
    /**
     * @description Обработать запрос на добавление в лист ожидания нового пациента.
     * @param {WaitingNewPatientRequest} request Запрос.
     * @returns {void}
     */
    onSubmitWaitingNewPatient(request) {
      const onResponse = (result) => {
        this.isAwaitWaitingRequest = false;
        result.forEach(({ message }) => {
          this.closeWaitingForm();
          this.notifySuccess(message);
          this.fetchWaitingList(true);
          this.fetchPatientList();
          this.selectedWaitings = this.waitingList;
        });
        result.forEachFail((error) => {
          this.waitingListInputErrors = Object.fromEntries(error.causes) || {};
          if (error.causes.length === 0) {
            this.notifyFail(error.title);
          }
        });
      };
      this.isAwaitWaitingRequest = true;
      const saving = this.selectedWaitingId !== null
        ? this.$waitingListGateway.editWithNewPatient(request, this.selectedWaitingId)
        : this.$waitingListGateway.createWithNewPatient(request);
      saving.then(onResponse);
    },
    /**
     * @description Получить список пациентов для листа ожидания.
     * @param {string|null} searchQuery
     * @returns {any[]}
     */
    fetchWaitingListPatientList(searchQuery = null) {
      this.$appointmentScheduleGateway.getPatientListFromAppointment(searchQuery)
        .then((patients) => {
          this.waitingListPatientList = patients;
        })
        .finally(() => { this.isWaitingListLoadingPatientList = false; });
      return [];
    },
    /**
     * @description Обработать просмотр записи из листа ожидания.
     * @param {number} id Идентификатор.
     * @returns {void}
     */
    async onViewWaiting(id) {
      const item = this.waitingList.find((el) => el.id === id);
      if (item) {
        this.waiting = item;
        return;
      }
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = (waiting) => {
        this.waiting = waiting;
      };
      const either = await this.$waitingListGateway.getById(id);
      either.fold(onFail, onSuccess);
    },
    /**
     * @description Открыть лист ожидания.
     * @type {(number[]|undefined)}
     * @returns {void}
     */
    openWaitingList(requestedIds) {
      const needsToggle = Array.isArray(requestedIds) !== true;
      if (needsToggle) {
        this.selectedWaitings = this.showWaitingList !== true
          ? this.waitingList
          : null;
      } else if (requestedIds.length > 1) {
        this.selectedWaitings = this
          .waitingList
          .filter((item) => requestedIds.includes(item.id));
      } else if (requestedIds.length === 1) {
        requestedIds.forEach(this.onViewWaiting);
      }
      this.onCloseAppointmentForm();
    },
  },
};

/**
 * @description Продолжительности.
 */
const presetsDuration = [
  {
    title: '15 мин',
    value: '00:15',
  },
  {
    title: '30 мин',
    value: '00:30',
  },
  {
    title: '45 мин',
    value: '00:45',
  },
  {
    title: '1 час',
    value: '01:00',
  },
  {
    title: '1,5 часа',
    value: '01:30',
  },
  {
    title: '2 часа',
    value: '02:00',
  },
];

/**
 * @description Примесь записей на прием.
 */
export default {
  presetsDuration,
  computed: {
    /**
     * @description Дата записи на прием прошла.
     * @returns {boolean}
     */
    appointmentIsOutdated() {
      if (this.wasSelectedAggregatorOrder) {
        const [isOutdated, dateTime] = this
          .selectedAppointment
          .fold([false, Date.now()])(
            (appointment) => [appointment.isOutdated, appointment.dateTime],
          );
        if (!isOutdated) {
          return false;
        }
        const startsAt = new Date(
          normalizeIso8601ToDateTimeStringFormat(
            `${convertToInputDate(this.appointmentDate)} ${this.appointmentTimeStarts}`,
          ),
        );
        return dateTime >= startsAt;
      }
      return false;
    },
    /**
     * @description Заголовок диалога редактирования записи на прием.
     * @returns {string}
     */
    dialogAppointmentTitle() {
      return this.wasSelectedAppointment ? 'Редактирование записи' : 'Новая запись';
    },
    /**
     * @description Выбрана существующая запись на прием.
     * @returns {boolean}
     */
    wasSelectedAppointment() {
      return this
        .selectedAppointment
        .fold(false)((candidate) => !!candidate.id);
    },
    /**
     * @description Идентификатор записи на прием.
     * @type {number}
     */
    initialAppointmentId() {
      const appointmentId = this.$route.query.appointment;
      return appointmentId ? +appointmentId : null;
    },
    /**
     * @description Список докторов.
     * @returns {any[]}
     */
    doctors: ({ doctorList, fetchDoctorList }) => doctorList.orLazy(fetchDoctorList),
    /**
     * @description Список пациентов.
     * @returns {any[]}
     */
    patients: ({ patientList, fetchPatientList }) => patientList.orLazy(fetchPatientList),
    /**
     * @description Выбранный период.
     * @returns {string}
     */
    period: ({ $route }) => $route.query.period || 'day',
    /**
     * @description Список услуг.
     * @returns {any[]}
     */
    services: ({ serviceList, fetchServiceList }) => serviceList.orLazy(fetchServiceList),
    /**
     * @description Выбрана заявка на прием от агрегатора.
     * @returns {boolean}
     */
    wasSelectedAggregatorOrder() {
      return this
        .selectedAppointment
        .fold(false)((appointment) => !!appointment.aggregatorOrderId);
    },
    /**
     * @description Список избранных услуг.
     * @returns {Maybe<ServiceItem>}
     */
    servicesWithFavorites: ({
      serviceListWithFavorites, fetchServiceFavoriteList,
    }) => serviceListWithFavorites.orLazy(fetchServiceFavoriteList),
  },
  data() {
    const day = new Date(this.$route.query.day || this.$options.today);
    return {
      day,
      /**
       * @description Сайд-бар свернут.
       * @type {boolean}
       */
      isCollapsedSideBar: false,
      /**
       * @description Список ближайших записей на прием.
       * @type {any[]}
       */
      appointmentNearList: [],
      /**
       * @description Список докторов для приема.
       * @type {Maybe}
       */
      doctorList: Maybe.None(),
      /**
       * @description Перемещаемая запись на прием.
       * @type {any}
       */
      dragAppointment: {
        employeeId: 0,
        id: 0,
        ratioToStep: 1,
      },
      /**
       * @description Нарушения ограничений.
       * @type {any}
       */
      inputErrors: {},
      /**
       * @description Ожидание завершения запроса на создание счета.
       * @type {boolean}
       */
      isAwaitCreateInvoice: false,
      /**
       * @description Ожидание завершения запроса.
       * @type {boolean}
       */
      isAwaitReloadTimetable: false,
      /**
       * @description Список пациентов.
       * @type {Maybe}
       */
      patientList: Maybe.None(),
      /**
       * @description Список услуг.
       * @type {Maybe}
       */
      serviceList: Maybe.None(),
      /**
       * @description Расширенные слоты.
       * @type {any[]}
       */
      expandedSlots: [],
      /**
       * @description Список избранных услуг.
       * @type {Maybe}
       */
      serviceListWithFavorites: Maybe.none(),
      /**
       * @description Временной шаг в минутах.
       * @type {number}
       */
      step: parseInt(this.$route.query.step || this.$options.defaultStep, 10),
      /**
       * @description Временная шкала.
       * @type {any[]}
       */
      timeline: [],
      /**
       * @description Необходимо подтверждение перед закрытием формы.
       * @type {any}
       */
      needConfirmBeforeClosed: {},
    };
  },
  defaultStep: 0,
  methods: {
    /**
     * @description Обработать нажатие на деактивированный слот.
     * @returns {void}
     */
    onClickDisabledCell() {
      this.notifyFail('Период недоступен для записи');
    },
    /**
     * @description Обработать открытие расписания на день.
     * @param {Date} day День.
     * @returns {void}
     */
    onOpenDay(day) {
      this.day = day;
    },
    /**
     * @description Обработать сворачивание сайд-бара.
     * @param {boolean} isCollapse Свернут.
     * @returns {void}
     */
    onSidebarCollapsed(isCollapse) {
      this.isCollapsedSideBar = isCollapse;
    },
    /**
     * @description Обработать изменение значений полей формы.
     * @param {string}  form     Название формы.
     * @param {boolean} isChange Есть изменения.
     * @returns {void}
     */
    onChangeFieldValues(form, isChange) {
      this.needConfirmBeforeClosed[form] = isChange;
    },
    /**
     * Вычислить самое раннее непустое время по слотам
     * @param {array} slots
     * @returns {string}
     */
    getPeriodStartBySlots(slots) {
      let from = '23:59';
      slots.forEach((slot) => {
        if (
          slot.starts_time < from
          && !slot.tags.includes('empty')
        ) {
          from = slot.starts_time;
        }
      });
      return from;
    },
    /**
     * Вычислить самое позднее непустое время по слотам
     * @param {array} slots
     * @returns {string}
     */
    getPeriodEndBySlots(slots) {
      let to = '00:00';
      slots.forEach((slot) => {
        if (
          slot.ends_time > to
          && !slot.tags.includes('empty')
        ) {
          to = slot.ends_time;
        }
      });
      return to;
    },
    /**
     * @description Создать запрос на создание записи на прием по источнику.
     * @param {Object}             appointment
     * @param {AppointmentRequest} request
     * @returns {AppointmentRequest}
     */
    createAppointmentCreationRequestBySource(appointment, request) {
      if (this.wasSelectedAggregatorOrder) {
        return new AggregatorAppointmentRequest(
          appointment.aggregatorOrderId,
          request,
        );
      }
      if (this.appointmentCopy && this.appointmentCopy.callId) {
        return new TelephonyCallAppointmentRequest(
          this.appointmentCopy.callId,
          request,
        );
      }
      return request;
    },
    /**
     * @description Отправить запрос на редактирование заявки на прием с существующим пациентом.
     * @param {AppointmentExistPatientCreationRequest} request Запрос.
     * @returns {Promise<Either>}
     */
    sendAppointmentExistsPatientRequest(request) {
      const appointment = this.selectedAppointment.fold({})((item) => item);
      if (this.wasSelectedAppointment) {
        return this.$appointmentScheduleGateway.editWithExistPatient(
          request,
          appointment.id,
        );
      }
      return this
        .$appointmentScheduleGateway
        .createWithExistPatient(
          this.createAppointmentCreationRequestBySource(appointment, request),
        );
    },
    /**
     * @description Распечатать новые записи пациента.
     * @param {number} patientId Идентификатор пациента.
     * @returns {any}
     */
    async printFutureAppointments(patientId) {
      const onFail = (response) => {
        this.notifyFail(response.title);
        return null;
      };
      const onSuccess = (response) => response;
      const either = await this.$appointmentScheduleGateway.viewFutureAppointmentsPrint(patientId);
      return either.fold(onFail, onSuccess);
    },
    /**
     * @description Отправить запрос на создание новой заявки на прием с новым пациентом.
     * @param {AppointmentNewPatientCreationRequest} request Запрос.
     * @returns {Promise<Either>}
     */
    sendAppointmentNewPatientCreationRequest(request) {
      const appointment = this.selectedAppointment.fold({})((item) => item);
      if (this.wasSelectedAppointment) {
        return this.$appointmentScheduleGateway.editWithNewPatient(
          this.appointmentEmployeeId,
          request,
          appointment.id,
        );
      }
      const appointmentCreationRequest = this.wasSelectedAggregatorOrder
        ? new AggregatorAppointmentRequest(appointment.aggregatorOrderId, request)
        : request;
      return this
        .$appointmentScheduleGateway
        .createWithNewPatient(appointmentCreationRequest);
    },
    /**
     * @description Получить данные визита.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {number} visitId    Идентификатор визита.
     * @returns {any}
     */
    async fetchAppointmentFromView(employeeId, visitId) {
      const onFail = (response) => {
        this.notifyFail(response.title);
      };
      const onSuccess = (visit) => visit;
      const either = await this.$appointmentScheduleGateway.getById(employeeId, visitId);
      return either.fold(onFail, onSuccess);
    },
    /**
     * @description Получить список докторов.
     * @returns {any[]}
     */
    fetchDoctorList() {
      this.$appointmentScheduleGateway
        .viewEmployeeListWithEnabledScheduling(this.isoDate, this.period)
        .then((list) => {
          this.doctorList = list;
        });
      return this.initialEmployees;
    },
    /**
     * @description Получить список пациентов.
     * @returns {any[]}
     */
    fetchPatientList() {
      this.$appointmentScheduleGateway.getPatientListFromAppointment().then((patients) => {
        this.patientList = patients;
      });
      return [];
    },
    /**
     * @description Получить список услуг.
     * @returns {any[]}
     */
    fetchServiceList() {
      this.$serviceGateway.allForView().then((list) => {
        this.serviceList = list;
      });
      return [];
    },
    /**
     * @description Получить услуги со списком избранных.
     * @returns {{all:ServiceItem[], favorites:Service[]}}
     */
    fetchServiceFavoriteList() {
      this.$serviceGateway.allWithFavoritesForView().then((list) => {
        this.serviceListWithFavorites = list;
      });
      return { all: [], favorites: [] };
    },
    /**
     * @description Открыть запись на прием.
     * @param {number}                  appointmentId Идентификатор записи на прием.
     * @param {string}                  date          Дата визита.
     * @param {{id:number,name:string}} employee      Сотрудник.
     * @param {string}                  timeEnds      Время окончания приема.
     * @param {string}                  timeStarts    Время начала приема.
     * @param {(number|null)}           cabinetId     Идентификатор кабинета
     * @returns {void}
     */
    async openAppointmentDialog(
      appointmentId,
      date,
      employee,
      timeEnds = '00:00',
      timeStarts = '00:00',
      cabinetId = null,
    ) {
      this.onCloseAppointmentForm();
      this.appointmentCabinetId = cabinetId;
      if (employee && employee.id) {
        this.appointmentEmployee = Maybe.fromEmpty(employee);
        this.appointmentWorkEmployees = [employee];
        this.onInputAppointmentEmployee(employee.id);
        this.onReloadEmployeeDayTimeline(employee.id, date, timeStarts);
      } else {
        this.appointmentEmployee = Maybe.none();
        this.onInputAppointmentEmployee(null);
      }
      if (appointmentId) {
        const appointment = await this.fetchAppointmentFromView(employee.id, appointmentId);
        const appointmentTimeStarts = appointment.timeStarts;
        this.onInputAppointmentTimes(appointmentTimeStarts, appointment.timeEnds);
        this.onInputAppointmentDate(
          new Date(
            normalizeIso8601ToDateTimeStringFormat(`${date} ${appointmentTimeStarts}`),
          ),
        );
        this.selectedAppointment = Maybe.fromEmpty(appointment);
      } else {
        this.onInputAppointmentTimes(timeStarts, timeEnds);
        this.onInputAppointmentDate(
          new Date(
            normalizeIso8601ToDateTimeStringFormat(`${date} ${timeStarts}`),
          ),
        );
        this.selectedAppointment = Maybe.some({
          actions: this.actions,
        });
      }
    },
    /**
     * @description Обработать начало перемещения записи на прием.
     * @param {any} appointment Запись на прием.
     * @returns {void}
     */
    onDragAppointmentStart(appointment) {
      this.dragAppointment = appointment;
    },
    /**
     * @description Обработать завершение перемещения записи на прием.
     * @returns {void}
     */
    onDragAppointmentEnd() {
      this.dragAppointment = {
        id: 0,
        ratioToStep: 1,
      };
    },
    /**
     * @description Обработать изменение шага.
     * @param {number} value
     * @returns {void}
     */
    onInputStep(value) {
      this.$goal(GOAL_CHANGE_APPOINTMENT_TIMELINE_STEP);
      this.$appointmentScheduleGateway.setTimetableStep(value);
      const query = { ...this.$route.query, step: value };
      this.$router.push({ query });
    },
    /**
     * @description Обработать приход пациента.
     * @param {number} appointmentId Идентификатор записи.
     * @returns {void}
     */
    onCameAppointment(appointmentId) {
      const onFail = (response) => {
        this.notifyFail(response.title);
      };
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.reloadTimetable(this.viewQuery);
        this.fetchPatientList();
      };
      this.$appointmentScheduleGateway.came(appointmentId).then((response) => {
        response.forEach(onSuccess);
        response.forEachLeft(onFail);
      });
    },
    /**
     * @description Обработать отмену визита.
     * @param {number} appointmentId         Идентификатор записи.
     * @param {string} comment               Комментарий.
     * @param {string} rejectionReasonId     Причина отмены.
     * @param {string} customRejectionReason Своя причина отмены.
     * @returns {void}
     */
    onCancelAppointment(
      appointmentId,
      comment,
      rejectionReasonId,
      customRejectionReason,
    ) {
      const onFail = (response) => {
        this.notifyFail(response.title);
        const failurePairs = response.causes.map((cause) => [cause.source, cause.details]);
        this.changeStatusInputErrors = Object.fromEntries(failurePairs) || {};
        this.appointmentInputErrors = Object.fromEntries(failurePairs) || {};
      };
      const onSuccess = (message) => {
        this.isShowCancelModal = false;
        this.notifySuccess(message);
        this.changeStatusInputErrors = {};
        this.reloadTimetable(this.viewQuery);
      };
      this
        .$appointmentScheduleGateway
        .cancel(appointmentId, comment, rejectionReasonId, customRejectionReason)
        .then((response) => {
          response.forEach(onSuccess);
          response.forEachLeft(onFail);
        });
    },
    /**
     * @description Обработать подтверждение записи на прием.
     * @param {number} appointmentId Идентификатор записи.
     * @returns {void}
     */
    onConfirmAppointment(appointmentId) {
      const onFail = (response) => {
        this.notifyFail(response.title);
      };
      const onSuccess = () => {
        this.notifySuccess('Запись на прием подтверждена');
        this.reloadTimetable(this.viewQuery);
      };
      this.$appointmentScheduleGateway.confirm(appointmentId).then((response) => {
        response.forEach(onSuccess);
        response.forEachLeft(onFail);
      });
    },
    /**
     * @description Обработать создание счета.
     * @param {number} appointmentId Идентификатор записи на прием.
     * @returns {void}
     */
    onCreateInvoice(appointmentId) {
      if (this.isAwaitCreateInvoice) { return; }
      this.isAwaitCreateInvoice = true;
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      const onSuccess = (response) => {
        this.notifySuccess(response.message);
        const params = { id: response.patientId, invoiceId: response.invoiceId, isNew: true };
        this.$router.push({ name: 'ClinicPanelPatientInvoice', params });
      };
      this
        .$invoiceGateway
        .createByAppointmentId(appointmentId)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitCreateInvoice = false; });
    },
    /**
     * @descriptionОткрыть список счетов пациента.
     * @param {number} id
     * @returns {void}
     */
    onOpenInvoices(id) {
      this.$router.push({ name: 'ClinicPanelPatientInvoiceList', params: { id } });
    },
    /**
     * @description Откатить запись до статуса "Не подтверждено".
     * @param {number} appointmentId Идентификатор записи.
     * @returns {void}
     */
    onNoConfirmAppointment(appointmentId) {
      const onFail = (response) => {
        this.notifyFail(response.title);
      };
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.reloadTimetable(this.viewQuery);
      };
      this.$appointmentScheduleGateway.noConfirm(appointmentId).then((response) => {
        response.forEach(onSuccess);
        response.forEachLeft(onFail);
      });
    },
    /**
     * @description Обработать открытие ближайшего визита.
     * @param {any} appointment
     * @returns {void}
     */
    onOpenNearAppointment(appointment) {
      this.openAppointmentDialog(
        appointment.id,
        appointment.date,
        {
          id: appointment.employee_id,
          name: '',
        },
        appointment.ends_time,
        appointment.starts_time,
      );
    },
    /**
     * @description Обработать открытие визита.
     * @returns {void}
     */
    async onOpenVisit() {
      this.selectedAppointment.forEach(async (appointment) => {
        await this.$router.push({
          name: 'ClinicPanelPatientVisit',
          params: {
            id: appointment.patientId,
            visitId: appointment.visitId,
          },
        });
      });
    },
    /**
     * @description Обработать запрос на редактирование записи на прием.
     * @param {AppointmentExistPatientCreationRequest} request Запрос.
     * @returns {void}
     */
    onSubmitAppointmentExistsPatient(request) {
      this.$route.params.patientId = '';
      this.$route.params.crmDealId = '';
      this.isAppointmentAwaitRequest = true;
      this.unsetPatientParam();
      const onResponse = (result) => {
        result.forEach((message) => {
          this.notifySuccess(message);
          this.reloadTimetable(this.viewQuery);
          this.fetchWaitingList();
          this.fetchPatientList();
        });
        result.forEachLeft((response) => {
          this.clarifyAndNotifyFail(response.interaction)(
            response.title,
            response.detail || '',
            response.telegramLink,
            response.whatsAppLink,
          );
          const failurePairs = response.causes.map((cause) => [cause.source, cause.details]);
          this.appointmentInputErrors = Object.fromEntries(failurePairs) || {};
        });
      };
      this
        .sendAppointmentExistsPatientRequest(request)
        .then(onResponse)
        .finally(() => { this.isAppointmentAwaitRequest = false; });
    },
    /**
     * @description Обработать запрос на создание записи на прием нового пациента.
     * @param {AppointmentNewPatientCreationRequest} request Запрос.
     * @returns {void}
     */
    onSubmitAppointmentNewPatient(request) {
      this.isAppointmentAwaitRequest = true;
      const onResponse = (result) => {
        result.forEach((message) => {
          this.notifySuccess(message);
          this.reloadTimetable(this.viewQuery);
          this.fetchPatientList();
        });
        result.forEachLeft((response) => {
          this.clarifyAndNotifyFail(response.interaction)(
            response.title,
            response.detail || '',
            response.telegramLink,
            response.whatsAppLink,
          );
          const failurePairs = response.causes.map((cause) => [cause.source, cause.details]);
          this.appointmentInputErrors = Object.fromEntries(failurePairs) || {};
        });
      };
      this
        .sendAppointmentNewPatientCreationRequest(request)
        .then(onResponse)
        .finally(() => { this.isAppointmentAwaitRequest = false; });
    },
    /**
     * @description Перенести запись на прием.
     * @param {MoveAppointmentRequest} request Запрос.
     * @returns {void}
     */
    moveAppointment(request) {
      const onResponse = (result) => {
        result.forEach((response) => {
          this.notifySuccess(response.message);
          this.reloadTimetable(this.viewQuery);
        });
        result.forEachFail((error) => {
          this.clarifyAndNotifyFail(error.interaction)(
            error.title,
            error.detail || '',
            error.telegramLink,
            error.whatsAppLink,
          );
        });
      };
      this.$appointmentScheduleGateway.moveAppointment(request).then(onResponse);
    },
  },
  mixins: [
    actionsMixin,
    aggregatorOrdersMixin,
    appointmentCopyMixin,
    appointmentCreationMixin,
    callMixin,
    nearestUnconfirmedAppointmentMixin,
    workTimeControlsMixin,
    freeDoctorControlsMixin,
    waitingListMixin,
  ],
  props: {
    initialEmployees: {
      default: () => [],
      type: Array,
    },
    prevCategory: {
      default: '',
      type: String,
    },
  },
  /**
   * @description Текущее время.
   * @type {Date}
   */
  today: new Date(),
  /**
   * @description Правила стили.
   * @type {minHeight:string}
   */
  rulerUnitCSS: {
    minHeight: `${STEP_HEIGHT}px`,
  },
};
